import { buildLoginRedirect, optionalAuthPage } from '../lib/page-runtime';
import type { IssueListItem, PaginatedData, CreateIssueRequest } from '../types/api';

(function() {
  const container = document.getElementById('issues-page');
  if (!container) return;

  // Read initial total from data attribute if available (for SSR display)
  const initialTotal = container.dataset.initialTotal;

  window.currentTab = 'open';
  window.currentSearch = '';
  window.currentPage = 1;
  let currentSort = 'newest';
  let currentPriority: string | null = null;
  let searchTimer: number | null = null;

  // Sync sort/priority from URL
  const urlParams = new URLSearchParams(window.location.search);
  const prefillRelatedSkillIdRaw = urlParams.get('related_skill_id');
  const prefillRelatedSkillId = prefillRelatedSkillIdRaw && /^\d+$/.test(prefillRelatedSkillIdRaw)
    ? parseInt(prefillRelatedSkillIdRaw, 10)
    : null;
  const newRequestPath = '/issues?new=1';
  const shouldOpenNewIssue = urlParams.get('new') === '1';
  if (urlParams.get('sort')) {
    currentSort = urlParams.get('sort')!;
  }
  if (urlParams.get('priority')) {
    currentPriority = urlParams.get('priority')!;
  }

  function renderTabBar() {
    const bar = document.getElementById('status-tab-bar');
    if (!bar) return;
    bar.querySelectorAll('.tab-btn').forEach(btn => {
      const t = btn.getAttribute('data-tab');
      btn.classList.toggle('active', t === window.currentTab);
    });
  }

  async function loadTabCounts() {
    const result = await window.IssuesPageData.loadTabCounts();
    if (!result.ok) return;
    const openEl = document.getElementById('tab-count-open');
    const closedEl = document.getElementById('tab-count-closed');
    if (openEl) openEl.textContent = String(result.data.open);
    if (closedEl) closedEl.textContent = String(result.data.closed);
  }


  function updateFilterSummary(total: number) {
    const wrapper = document.getElementById('issues-filter-summary');
    const text = document.getElementById('issues-filter-summary-text');
    const clear = document.getElementById('clear-issue-filters');
    if (!wrapper || !text || !clear) return;

    const summary = window.IssuesPageRenderers.filterSummaryText({
      total,
      view: window.currentTab === 'open' ? 'open' : 'closed',
      priority: currentPriority,
      search: window.currentSearch,
    });
    if (!summary) {
      wrapper.classList.add('hidden');
      text.textContent = '';
      return;
    }

    wrapper.classList.remove('hidden');
    text.textContent = summary;
  }

  function filteredEmptyMessage(): string {
    return window.IssuesPageRenderers.emptyMessage({
      view: window.currentTab === 'open' ? 'open' : 'closed',
      priority: currentPriority,
      search: window.currentSearch,
    });
  }

  function clearIssueFilters() {
    currentPriority = null;
    window.currentSearch = '';
    window.currentPage = 1;
    const search = document.getElementById('search-input') as HTMLInputElement | null;
    if (search) search.value = '';
    document.querySelectorAll('.priority-chip').forEach(chip => chip.classList.remove('active'));
    loadIssues();
  }

  function updateNewIssueVisibility(loggedIn: boolean) {
    const btn = document.getElementById('new-issue-btn');
    const loginLink = document.getElementById('new-issue-login-link') as HTMLAnchorElement | null;
    if (btn) btn.style.display = loggedIn ? '' : 'none';
    if (loginLink) {
      loginLink.style.display = loggedIn ? 'none' : '';
      loginLink.href = buildLoginRedirect(newRequestPath);
    }
  }

  function switchTab(tab: string) {
    window.currentTab = tab;
    window.currentPage = 1;
    renderTabBar();
    loadIssues();
  }

  window.switchTab = switchTab;

  window.__onSortChange = function(value: string): void {
    currentSort = value;
    window.currentPage = 1;
    loadIssues();
  };

  window.__togglePriority = function(value: string): void {
    currentPriority = currentPriority === value ? null : value;
    window.currentPage = 1;
    // Update chip active states
    document.querySelectorAll('.priority-chip').forEach(chip => {
      const p = chip.getAttribute('data-priority');
      chip.classList.toggle('active', p === currentPriority);
    });
    loadIssues();
  };

  function searchIssues(query: string) {
    clearTimeout(searchTimer!);
    searchTimer = window.setTimeout(() => {
      window.currentSearch = query;
      window.currentPage = 1;
      loadIssues();
    }, 300);
  }

  window.searchIssues = searchIssues;
  window.__clearIssueFilters = clearIssueFilters;

  async function loadIssues() {
    const view = window.currentTab === 'open' ? 'open' : 'closed';

    const result = await window.IssuesPageData.loadIssues({
      page: window.currentPage,
      limit: 15,
      view,
      sort: currentSort,
      priority: currentPriority,
      search: window.currentSearch,
    });

    if (result.ok) {
      updateFilterSummary(result.data.total);
      renderIssues(result.data);
    } else {
      document.getElementById('issues-list')!.innerHTML = window.IssuesPageRenderers.renderError('Failed to load skill requests');
    }
  }

  window.loadIssues = loadIssues;

  function renderIssues(data: PaginatedData<IssueListItem>) {
    const list = document.getElementById('issues-list');
    const meta = document.getElementById('issues-meta-total');
    if (meta) {
      const label = window.currentTab === 'open' ? 'open requests' : 'closed requests';
      meta.textContent = data.total + ' ' + label;
    }
    if (!data.items.length) {
      list!.innerHTML = '<div class="card">' + emptyStateHtml('fa-inbox', filteredEmptyMessage()) + '</div>';
      document.getElementById('pagination')!.innerHTML = '';
      return;
    }

    list!.innerHTML = window.IssuesPageRenderers.renderIssues(data.items);

    const pg = document.getElementById('pagination');
    if (pg) pg.innerHTML = window.IssuesPageRenderers.renderPagination(data);
  }

  async function submitNewIssue(e: Event) {
    e.preventDefault();
    const title = (document.getElementById('modal-issue-title') as HTMLInputElement).value.trim();
    const description = (document.getElementById('modal-issue-desc') as HTMLTextAreaElement).value.trim();
    const priority = (document.getElementById('modal-issue-priority') as HTMLSelectElement).value as 'low' | 'medium' | 'high';
    const acceptanceCriteria = (document.getElementById('modal-issue-ac') as HTMLTextAreaElement | null)?.value.trim() || '';
    const btn = document.getElementById('modal-submit-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Creating...';

    try {
      const requestData: CreateIssueRequest = {
        title,
        description,
        priority,
        ...(acceptanceCriteria && { acceptance_criteria: acceptanceCriteria }),
        ...(prefillRelatedSkillId ? { related_skill_id: prefillRelatedSkillId } : {}),
      };

      const result = await window.IssuesPageData.createIssue(requestData);

      if (result.ok) {
        showToast('Skill request created!');
        document.getElementById('submit-modal')!.classList.add('hidden');
        window.location.href = '/issues/' + result.data.id;
      } else {
        showToast(result.error, 'error');
      }
    } catch(e) {
      showToast('Network error', 'error');
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.innerHTML = '<i class="fas fa-paper-plane mr-2"></i>Create request';
    }
  }

  window.submitNewIssue = submitNewIssue;

  optionalAuthPage({
    onAuth: function(ctx) { updateNewIssueVisibility(ctx.loggedIn); },
    init: function(ctx) {
      updateNewIssueVisibility(ctx.loggedIn);
      if (ctx.loggedIn && shouldOpenNewIssue) {
        document.getElementById('submit-modal')?.classList.remove('hidden');
      }
      renderTabBar();
      loadTabCounts();
      // Sync sort dropdown from state
      const sortSelect = document.getElementById('sort-select') as HTMLSelectElement;
      if (sortSelect) sortSelect.value = currentSort;
      // Sync priority chip active state
      if (currentPriority) {
        document.querySelectorAll('.priority-chip').forEach(chip => {
          chip.classList.toggle('active', chip.getAttribute('data-priority') === currentPriority);
        });
      }
      // Always load from API for consistency
      loadIssues();
    },
  });
})();
